import React, { useEffect, useRef, useState } from 'react';
import { installSunshine } from '../utils/sunshine';

// Admin modal that installs/updates Sunshine on a devkit and streams the
// setup-script log live. Styled to match SunshineManager (dark panel,
// gradient header). On success, offers to pair immediately.

export default function SunshineInstall({ devkit, onClose }) {
	const ip = devkit?.tailscale?.ipv4?.[0];
	const name = devkit?.vessel_name || `DevKit ${devkit?.devkit_id ?? ''}`;

	const [lines, setLines] = useState([]);
	const [state, setState] = useState('running'); // running | success | failed
	const logRef = useRef(null);
	const closeRef = useRef(null);

	useEffect(() => {
		if (!ip) return;
		setLines([`Connecting to ${ip}…`]);
		closeRef.current = installSunshine(ip, {
			devkitId: devkit?.devkit_id,
			onLine: (line) => setLines((prev) => [...prev, line]),
			onDone: (exit) => setState(exit === 0 ? 'success' : 'failed'),
		});
		return () => closeRef.current?.();
	}, [ip]);

	// Autoscroll the log to the bottom as lines arrive
	useEffect(() => {
		if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
	}, [lines]);

	return (
		<div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
			<div className='bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden animate-in fade-in zoom-in duration-200 flex flex-col'>
				{/* Header */}
				<div className='bg-gradient-to-r from-gray-800 via-gray-900 to-black border-b border-gray-700 shadow-md text-white p-6 relative flex-shrink-0'>
					<button
						onClick={onClose}
						className='absolute top-4 right-4 text-white hover:bg-white/20 rounded-full p-2 transition-colors'
					>
						<svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
							<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
						</svg>
					</button>
					<div className='flex items-center gap-3'>
						<svg className='w-8 h-8 text-[#EBEBEB]' fill='none' stroke='currentColor' viewBox='0 0 24 24' strokeWidth={2}>
							<path strokeLinecap='round' strokeLinejoin='round' d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12' />
						</svg>
						<h2 className='text-2xl font-bold'>Install Sunshine</h2>
					</div>
					<p className='mt-2 text-[#EBEBEB]'>
						{name} · {ip}
					</p>
				</div>

				{/* Live log */}
				<div
					ref={logRef}
					className='flex-1 overflow-y-auto bg-slate-950 text-gray-200 font-mono text-xs leading-relaxed p-4'
					style={{ maxHeight: 'calc(90vh - 240px)', minHeight: '260px' }}
				>
					{lines.map((line, i) => (
						<div key={i} className='whitespace-pre-wrap break-words'>
							{line}
						</div>
					))}
				</div>

				{/* Footer */}
				<div className='flex-shrink-0 border-t border-gray-200 p-4 flex items-center justify-between gap-3'>
					<div className='flex items-center gap-2 text-sm'>
						{state === 'running' && (
							<>
								<svg className='animate-spin h-4 w-4 text-blue-600' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2}>
									<circle className='opacity-25' cx='12' cy='12' r='10' strokeLinecap='round' />
									<path className='opacity-75' d='M4 12a8 8 0 018-8' strokeLinecap='round' />
								</svg>
								<span className='text-gray-600'>Installing…</span>
							</>
						)}
						{state === 'success' && (
							<span className='text-emerald-600 font-medium'>
								✓ Installed — devkit is rebooting to finish setup (~1 min)
							</span>
						)}
						{state === 'failed' && (
							<span className='text-red-600 font-medium'>
								✗ Install failed — see log above
							</span>
						)}
					</div>
					<div className='flex gap-2'>
						<button
							onClick={onClose}
							className='px-4 py-2 text-sm font-medium rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors'
						>
							{state === 'running' ? 'Run in background' : 'Close'}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}
